import type { TranslateString } from '@/types/translation.types';

/** an `i18n/<lang>/email.json` bundle per language abbreviation */
export type EmailTranslations = Map<string, Record<string, string>>;

const EVENT_TITLE_TOKEN = /\{\{\s*eventTitle\s*\}\}/g;

export async function loadEmailTranslations(languages: string[]): Promise<EmailTranslations> {
  const loaded = await Promise.all(
    Array.from(new Set(languages)).map(async (language) => {
      try {
        const bundle = (await import(`../i18n/${language}/email.json`)).default as Record<string, string>;
        return [language, bundle] as const;
      } catch {
        return [language, {}] as const;
      }
    })
  );

  return new Map(loaded);
}

export interface BuildTranslateStringArgs {
  /** `<templateId>.<field>` key, or undefined when the value comes from `eventTitleFor` instead */
  key: string | undefined;
  fallback: string;
  languages: string[];
  baseLanguage: string;
  translations: EmailTranslations;
  eventTitleFor?: (language: string) => string;
}

export function buildTranslateString({
  key,
  fallback,
  languages,
  baseLanguage,
  translations,
  eventTitleFor,
}: BuildTranslateStringArgs): TranslateString {
  const value: TranslateString = { base: fallback, changed: '' };

  for (const language of languages) {
    const translated = key ? translations.get(language)?.[key] : eventTitleFor?.(language);
    value[language] = translated?.replace(EVENT_TITLE_TOKEN, eventTitleFor?.(language) ?? '') ?? fallback;
  }

  // the editor renders `base` for the base language
  value.base = (value[baseLanguage] as string) || fallback;

  return value;
}
